/**
 * OrgChartCanvas — kanwa organigramu (React Flow), F24 Pakiet 0 rebase.
 *
 * PRZED F24 Pakiet 0: drzewo budowane z płaskiej listy pracowników po
 * `managerEmployeeId`, edytowalne (przeciągnij kafelek pracownika na innego = zmień
 * przełożonego). USUNIĘTE — `managerEmployeeId` jest dziś NADPISANIEM wyjątkowym
 * (domyślnie przełożony wynika z kierownika jednostki organizacyjnej, wyliczany po
 * stronie serwera), więc klient nie może już wiarygodnie zrekonstruować ani edytować
 * hierarchii z samych rekordów kadrowych — dawne `isAncestor()` po `managerEmployeeId`
 * dawałoby fałszywe blokady cykli. Zmiana przełożonego żyje teraz na karcie pracownika
 * (pole „Przełożony (nadpisanie)"), struktura jednostek — na ekranie „Struktura"
 * (`CompanyDetailDrawer.orgUnits.tsx`).
 *
 * PO: drzewo budowane z `GET /org-units/tree` (jednostki organizacyjne, `OrgUnitNode`)
 * + pracownicy pogrupowani pod SWOJĄ jednostką jako liście (`EmployeeNode`). WYŁĄCZNIE
 * podgląd — bez przeciągania, bez edycji (build celowo prosty, patrz brief F24 Pakiet 0).
 * Pracownicy bez przypisanej jednostki (`orgUnitId === null`) wiszą pod syntetycznym
 * węzłem-koszem „Nieprzypisani" (widoczny tylko gdy tacy pracownicy istnieją).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Background, Controls, type Edge, MiniMap, type Node, ReactFlow } from '@xyflow/react';

import { EmployeeNode, type EmployeeNodeData } from '@/components/org-chart/EmployeeNode';
import { OrgUnitNode, type OrgUnitNodeData } from '@/components/org-chart/OrgUnitNode';
import { autoLayout } from '@/components/process-builder/autoLayout';
import type { EmployeeListItem, OrgUnitTreeNode } from '@/lib/use-tenant-people';

// Stała referencja modułowa — wymóg React Flow dla nodeTypes.
const ORG_NODE_TYPES = { employee: EmployeeNode, orgUnit: OrgUnitNode };
const EDGE_OPTIONS = { type: 'smoothstep' as const };
const UNIT_NODE_SIZE = { width: 220, height: 76 };
const EMPLOYEE_NODE_SIZE = { width: 220, height: 68 };

const UNASSIGNED_UNIT_ID = '__unassigned__';

function initialsOf(first: string, last: string): string {
  const a = first.trim().charAt(0);
  const b = last.trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

/** Zbiór id wszystkich jednostek obecnych w drzewie (rekurencyjnie, wliczając dzieci). */
function collectUnitIds(units: OrgUnitTreeNode[], into: Set<string>): void {
  for (const unit of units) {
    into.add(unit.id);
    collectUnitIds(unit.children, into);
  }
}

/**
 * Budowa surowego grafu (węzły + krawędzie, BEZ layoutu — pozycje `{x:0,y:0}`, layout
 * dolicza `autoLayout` w komponencie) z drzewa jednostek organizacyjnych + pracowników.
 * Czysta funkcja — testowalna bez renderu React Flow (jsdom go nie lubi — mirror
 * `snapshotNodesToFlowNodes` w `ProcessGraphViewer.tsx`).
 *
 * Pracownik z ustawionym `orgUnitId`, który NIE jest węzłem drzewa (jednostka
 * nieaktywna/soft-deleted, ale wciąż referencjonowana) trafia do koszyka
 * „Nieprzypisani" — inaczej zniknąłby z organigramu bez śladu.
 */
export function buildOrgChartGraph(
  tree: OrgUnitTreeNode[],
  employees: EmployeeListItem[],
  positionLabelByCode: Map<string, string> | undefined,
  t: (key: string) => string,
): { nodes: Node[]; edges: Edge[] } {
  const unitIdsInTree = new Set<string>();
  collectUnitIds(tree, unitIdsInTree);

  const employeesByUnit = new Map<string, EmployeeListItem[]>();
  for (const e of employees) {
    const key =
      e.orgUnitId !== null && unitIdsInTree.has(e.orgUnitId) ? e.orgUnitId : UNASSIGNED_UNIT_ID;
    const list = employeesByUnit.get(key) ?? [];
    list.push(e);
    employeesByUnit.set(key, list);
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  function addEmployeeNodes(unitId: string) {
    for (const e of employeesByUnit.get(unitId) ?? []) {
      const nodeId = `emp:${e.id}`;
      nodes.push({
        id: nodeId,
        type: 'employee',
        position: { x: 0, y: 0 },
        data: {
          name: `${e.firstName} ${e.lastName}`.trim(),
          position: e.positionCode
            ? (positionLabelByCode?.get(e.positionCode) ?? e.positionCode)
            : null,
          initials: initialsOf(e.firstName, e.lastName),
        } satisfies EmployeeNodeData,
      });
      edges.push({ id: `unit:${unitId}->${nodeId}`, source: `unit:${unitId}`, target: nodeId });
    }
  }

  function walk(units: OrgUnitTreeNode[], isRootLevel: boolean) {
    for (const unit of units) {
      const nodeId = `unit:${unit.id}`;
      nodes.push({
        id: nodeId,
        type: 'orgUnit',
        position: { x: 0, y: 0 },
        data: {
          name: unit.name,
          kind: unit.kind,
          kindLabel: t(`tenant.settings.orgStructure.kind.${unit.kind}`),
          managerName: unit.managerName,
          isRoot: isRootLevel,
        } satisfies OrgUnitNodeData,
      });
      if (unit.parentId) {
        edges.push({
          id: `unit:${unit.parentId}->${nodeId}`,
          source: `unit:${unit.parentId}`,
          target: nodeId,
        });
      }
      addEmployeeNodes(unit.id);
      walk(unit.children, false);
    }
  }

  walk(tree, true);

  // Pracownicy bez jednostki (albo z jednostką poza drzewem) — syntetyczny węzeł-kosz,
  // TYLKO gdy tacy istnieją.
  const unassigned = employeesByUnit.get(UNASSIGNED_UNIT_ID) ?? [];
  if (unassigned.length > 0) {
    nodes.push({
      id: `unit:${UNASSIGNED_UNIT_ID}`,
      type: 'orgUnit',
      position: { x: 0, y: 0 },
      data: {
        name: t('tenant.orgChart.unassignedUnit'),
        kind: 'department',
        kindLabel: '',
        managerName: null,
        isRoot: true,
      } satisfies OrgUnitNodeData,
    });
    addEmployeeNodes(UNASSIGNED_UNIT_ID);
  }

  return { nodes, edges };
}

export interface OrgChartCanvasProps {
  /** Drzewo jednostek organizacyjnych firmy (`GET /org-units/tree`). */
  tree: OrgUnitTreeNode[];
  /** Wszyscy pracownicy firmy (do pogrupowania pod jednostkami po `orgUnitId`). */
  employees: EmployeeListItem[];
  /** Etykieta stanowiska (słownik `employee_positions`) — `code -> label`, opcjonalna. */
  positionLabelByCode?: Map<string, string>;
}

export function OrgChartCanvas({ tree, employees, positionLabelByCode }: OrgChartCanvasProps) {
  const { t } = useTranslation();

  const { laidNodes, computedEdges } = useMemo(() => {
    const { nodes: rawNodes, edges: rawEdges } = buildOrgChartGraph(
      tree,
      employees,
      positionLabelByCode,
      t,
    );

    const sizeByNode = new Map<string, { width: number; height: number }>();
    for (const n of rawNodes) {
      sizeByNode.set(n.id, n.type === 'employee' ? EMPLOYEE_NODE_SIZE : UNIT_NODE_SIZE);
    }

    const laid = autoLayout(rawNodes, rawEdges, {
      direction: 'TB',
      nodesep: 50,
      ranksep: 90,
      // `autoLayout` przyjmuje JEDEN `nodeSize` fallback (bez per-node mapy). Węzły organigramu
      // są układane przed pomiarem (bez `measured`), a jednostka i pracownik mają zbliżone
      // wymiary (220×~70), więc wspólny szacunek wystarcza.
      nodeSize: UNIT_NODE_SIZE,
    });
    return { laidNodes: laid, computedEdges: rawEdges };
  }, [tree, employees, positionLabelByCode, t]);

  return (
    <ReactFlow
      nodes={laidNodes}
      edges={computedEdges}
      nodeTypes={ORG_NODE_TYPES}
      defaultEdgeOptions={EDGE_OPTIONS}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      fitView
      minZoom={0.2}
      maxZoom={1.5}
      proOptions={{ hideAttribution: true }}
    >
      <Background />
      <Controls showInteractive={false} />
      <MiniMap pannable zoomable />
    </ReactFlow>
  );
}
